import RadioButtonUncheckedOutlinedIcon from '@mui/icons-material/RadioButtonUncheckedOutlined';
import {Autocomplete, FormControl, TextField, Grid, Tabs, Tab, IconButton} from '@mui/material';
import PolylineOutlinedIcon from '@mui/icons-material/PolylineOutlined';
import React, { useState, useEffect, Fragment, useRef } from 'react';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {useTranslation} from "react-i18next";
import VectorSource from 'ol/source/Vector';
import VectorLayer from 'ol/layer/Vector';
import Draw from 'ol/interaction/Draw.js';
import TileLayer from 'ol/layer/Tile';
import {Control, ScaleLine, defaults as defaultControls} from 'ol/control';
import CountryCombo from '../CountryCombo.js';
import {observer} from 'mobx-react';
import {fromLonLat} from 'ol/proj';
import OSM from 'ol/source/OSM';
import View from 'ol/View';
import Map from 'ol/Map';
import 'ol/ol.css';
import {useGlobalStorage} from '../mobx/GlobalStorage.js';
import {TAB_PLACE} from './SearchCommon.js';
import {getCityName} from '../Trans.js';
import {COUNTRY_ID_FI} from '../Util.js';

const TAB_NAMES		= 0;
const TAB_MAP		= 1;

function FilterPlaceNames(props) {
	const {t} = useTranslation('obs');
	const gStorage = useGlobalStorage();
	const [cityList, setCityList] = useState([]);

	useEffect(() => {
		const country_id = props.filter.country ? props.filter.country.id : COUNTRY_ID_FI;
		setCityList(gStorage.lists.citylist.list.filter(x => x.country === country_id).sort(citySort));
		// eslint-disable-next-line
	}, [props.filter.country]);

	function searchGetCityName(city) {
		return getCityName(city, gStorage.conn.user);
	}

	function citySort(c1, c2) {
		return searchGetCityName(c1).localeCompare(searchGetCityName(c2), 'fi');
	}

	/* ------------------------------------------------------------------ */

	if (gStorage.main.filter.place_tab !== TAB_NAMES)
		return null;

	return(
		<Fragment>
			<Grid size={{xs: 12}} align={'center'}>
				<FormControl variant="outlined" style={{width: 300, margin: 8}}>
					<CountryCombo
						value={props.filter.country ? props.filter.country.id : 0}
						onChange={props.countryChange}
						allowNone
						style={{textAlign: 'left'}}
					/>
				</FormControl>
			</Grid>
			<Grid size={{xs: 12}} align={'center'}>
				<FormControl variant="outlined" style={{width: 300, margin: 8}}>
					<Autocomplete
						multiple
						size='small'
						onChange={props.cityChange}
						options={cityList}
						disabled={cityList.length <= 0}
						getOptionLabel={(dt) => searchGetCityName(dt)}
						isOptionEqualToValue={(dt, val) => dt.id === val.id}
						defaultValue={[]}
						value={props.filter.cities}
						filterSelectedOptions
						renderInput={(params) => (
							<TextField
								{...params}
								label={t('filter.cities')}
							/>
						)}
					/>
				</FormControl>
			</Grid>
			<Grid size={{xs: 12}} align={'center'}>
				<FormControl variant="outlined" style={{width: 300, margin: 8}}>
					<Autocomplete
						multiple
						size='small'
						onChange={props.assoChange}
						options={gStorage.lists.assolist.list}
						disabled={props.filter.country && props.filter.country.id !== COUNTRY_ID_FI}
						getOptionLabel={(dt) => dt.name}
						isOptionEqualToValue={(dt, val) => dt.id === val.id}
						defaultValue={[]}
						value={props.filter.asso}
						filterSelectedOptions
						renderInput={(params) => (
							<TextField
								{...params}
								label={t('filter.associations')}
							/>
						)}
					/>
				</FormControl>
			</Grid>
		</Fragment>
	);
};

/* -------------------------------------------------------------------------- */

const DT_POLYGON	= 0;
const DT_CIRCLE		= 1;

function getElemHeight(node) {
	const list = [
		'margin-top',
		'margin-bottom',
		'border-top',
		'border-bottom',
		'padding-top',
		'padding-bottom',
		'height'
	]

	if (!node) {
		console.error('getElemHeight: node', node);
		return 0;
	}

	const style = window.getComputedStyle(node)
	return list
		.map(k => parseInt(style.getPropertyValue(k), 10))
		.reduce((prev, cur) => prev + cur)
}

const DrawToolbar = ((props) => {
	const gStorage = useGlobalStorage();
	const f = gStorage.main.filter;

	function setPolygon() {
		f.setDrawType(DT_POLYGON);
	}

	function setCircle() {
		f.setDrawType(DT_CIRCLE);
	}

	function clearSel() {
		props.clearSel();
	}

	return (
		<div
		    ref={props.ctrlRef}
		    className='ol-custom ol-control' 
		    id='draw-tool-bar' 
		    style={{top: 8, left: f.map_w - 40, width: 32, height: 64}}>
		    <IconButton
			style={{top: 0, left: 0, width: 32, height: 32}}
			onClick={setPolygon}>
			<PolylineOutlinedIcon/>
		    </IconButton>
		    <IconButton
			style={{top: 4, left: 0, width: 32, height: 32}}
			onClick={setCircle}>
			<RadioButtonUncheckedOutlinedIcon/>
		    </IconButton>
		    <IconButton
			style={{top: 8, left: 0, width: 32, height: 32}}
			onClick={clearSel}>
			<DeleteOutlineIcon/>
		    </IconButton>
		</div>
	);
});

const FilterPlaceMap = observer((props) => {
	const [resizeDone, setResizeDone] = useState(true);
	const gStorage = useGlobalStorage();
	const f = gStorage.main.filter;
	const ctrlRef = useRef(null);
	const mapRef = useRef(null);
	
	function handleResize() {
		const h = getElemHeight(document.getElementById('appbar')) + 
			getElemHeight(document.getElementById('searchtab')) +
			getElemHeight(document.getElementById('placetab')) + 
			getElemHeight(document.getElementById('bottomg')) + 8;

		f.setMapSize(window.innerWidth, window.innerHeight - h);
		setResizeDone(true);
	}

	function updateViewport() {
		const vs = f.map.getLayers().getArray()[1].getSource();

		if (!props.features.length) 
			return;

		f.map.getView().fit(
			vs.getExtent(), {
				size: f.map.getSize(),
				maxZoom: 16,
				padding: [32, 32, 32, 32]
			}
		);
	}

	function addDrawInteraction(map) {
		const ia = map.getInteractions();

		while (ia.getLength() > 9) {
			map.removeInteraction(ia.getArray()[9]);
		}

		const vs = map.getLayers().getArray()[1].getSource();
		const draw = new Draw({
			source: vs,
			type: f.draw_type === DT_CIRCLE ? 'Circle' : 'Polygon',
		});

		map.addInteraction(draw);

		draw.on('drawend', (evt) => { 
			const f = gStorage.main.filter;
			const vs = f.map.getLayers().getArray()[1].getSource();

			let a = vs.getFeatures();
			a.push(evt.feature);
			props.setCoordinates(a);
		});
	}

	useEffect(() => {
		const vs = new VectorSource({wrapX: false, features: props.features});

		const map = new Map({
			controls: defaultControls({rotate: false, zoom: false}).extend([new ScaleLine({units: 'metric'})]),
			layers: [
				new TileLayer({
					source: new OSM(),
				}),
				new VectorLayer({
					source: vs,
				}),
			],
			view: new View({
				center: fromLonLat([25.1, 60.9]),
				zoom: 8,
			}),
			moveTolerance: 8
		});

		/* ---------------------------------------------------------- */

		addDrawInteraction(map);

		/* ---------------------------------------------------------- */

		const clh = getElemHeight(document.getElementById('appbar')) + 
			getElemHeight(document.getElementById('searchtab')) +
			getElemHeight(document.getElementById('placetab')) + 
			getElemHeight(document.getElementById('bottomg')) + 8;

		f.setMap(map);
		f.setMapSize(window.innerWidth, window.innerHeight - clh);
		f.setDrawType(DT_POLYGON);

		setResizeDone(false);

		/* ---------------------------------------------------------- */

		window.addEventListener('resize', handleResize);

		return (() => {
			window.removeEventListener('resize', handleResize);
		});
		// eslint-disable-next-line
	}, []);

	useEffect(() => {
		if (mapRef.current) {
			f.map.setTarget(mapRef.current);

			if (ctrlRef.current) {
				while (f.map.getControls().getLength() > 2) {
					f.map.removeControl(f.map.getControls().getArray()[2]);
				}

				f.map.addControl(new Control({element: ctrlRef.current}));
			}

			const vs = f.map.getLayers().getArray()[1].getSource();

			if (vs.getFeatures().length > 0)
				return;

			vs.addFeatures(props.features);
		}
		// eslint-disable-next-line
	}, [mapRef.current, ctrlRef.current, f.tab, f.place_tab]);

	useEffect(() => {
		const vs = f.map.getLayers().getArray()[1].getSource();

		while (vs.getFeatures().length > 0) {
			vs.removeFeature(vs.getFeatures()[0]);
		}

		vs.addFeatures(props.features);
		// eslint-disable-next-line
	}, [props.features]);

	useEffect(() => {
		addDrawInteraction(f.map);
		// eslint-disable-next-line
	}, [f.draw_type]);

	useEffect(() => {
		if (resizeDone === false) {
			updateViewport();
		} else {
			setResizeDone(false);
		}
		// eslint-disable-next-line
	}, [resizeDone]);

	function clearSel() {
		props.clearSel();
		addDrawInteraction(f.map);
	}
	
	/* ------------------------------------------------------------------ */

	if (f.place_tab !== TAB_MAP)
		return null;

	return (
		<div ref={mapRef} id="mapContainer" style={{width: f.map_w, height: f.map.h}} >
			<DrawToolbar 
				filter={f} 
				ctrlRef={ctrlRef} 
				setCoordinates={props.setCoordinates} 
				clearSel={clearSel}/>
		</div>
	);
});

/* -------------------------------------------------------------------------- */

const FilterPlace = observer((props) => {
	const gStorage = useGlobalStorage();
	const {t} = useTranslation('obs');

	const handleTabChange = (event, tab) => {
		gStorage.main.filter.setPlaceTab(tab);
	}

	if (gStorage.main.filter.tab !== TAB_PLACE)
		return null;

	return (
		<Grid container direction="column" justifyContent="center" alignItems="center" spacing={0}>
			<Grid align={'center'}>
				<Tabs
					id="placetab"
					value={gStorage.main.filter.place_tab} 
					onChange={handleTabChange} 
					align={'center'}
					indicatorColor="primary"
					textColor="primary"
					scrollButtons="auto"
					variant="scrollable"
					sx={{m: 0, p: 0}}>
					<Tab 
						value={TAB_NAMES} 
						label={t('filter.names')}/>

					<Tab 
						value={TAB_MAP} 
						label={t('filter.map')}/>
				</Tabs>
			</Grid>

			<FilterPlaceNames
				filter={props.filter}
				countryChange={props.countryChange}
				cityChange={props.cityChange}
				assoChange={props.assoChange}
				/>

			<FilterPlaceMap 
				filter={props.filter}
				features={props.features}
				setCoordinates={props.setCoordinates} 
				clearSel={props.clearSel}
				/>
		</Grid>
	);
});

export default FilterPlace;
